"use client";
import { useForumType } from '@/components/hooks/useForumType';

import React from 'react';
import { useCurrentUser } from '../common/withUser';
import { tagGetUrl, getTagMinimumKarmaPermissions, tagUserHasSufficientKarma } from '../../lib/collections/tags/helpers';
import { slugify } from '@/lib/utils/slugify';
import { useLocation, useNavigate } from "@/lib/routeUtil";
import { useTagBySlug } from './useTag';
import { TagForm } from './TagForm';
import SingleColumnSection from "../common/SingleColumnSection";
import SectionTitle from "../common/SectionTitle";
import Loading from "../vulcan-core/Loading";
import { useMutation } from "@apollo/client/react";
import { gql } from "@/lib/generated/gql-codegen";
import { defineStyles, useStyles } from '../hooks/useStyles';

const TagEditFragmentUpdateMutation = gql(`
  mutation updateTagNewTagPage($selector: SelectorInput!, $data: UpdateTagDataInput!) {
    updateTag(selector: $selector, data: $data) {
      data {
        ...TagEditFragment
      }
    }
  }
`);

export const styles = defineStyles("NewTagPage", (_theme: ThemeType) => ({
  root: {
    position: "relative",
  },
}));

const NewTagPage = () => {
  const { forumType } = useForumType();
  const classes = useStyles(styles);
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const [updateTag] = useMutation(TagEditFragmentUpdateMutation);

  const { query } = useLocation();
  const createdType = ["tag","wiki"].includes(query.type) ? query.type : "tag";
  const prefillName = query.name ?? null;

  const { tag: existingTag, loading: loadingExistingTag } = useTagBySlug(
    slugify(prefillName),
    'TagEditFragment',
    { skip: !prefillName }
  );
  
  if (!currentUser) {
    return (
      <SingleColumnSection>
        <SectionTitle title={`New Wikitag`}/>
        <div>
          You must be logged in to define new wikitags.
        </div>
      </SingleColumnSection>
    );
  }
  
  if (!tagUserHasSufficientKarma(currentUser, "new", forumType)) {
    return (
      <SingleColumnSection>
        <SectionTitle title={`New Wikitag`}/>
        <div>
          You do not have enough karma to define new wikitags. You must have
          at least {getTagMinimumKarmaPermissions(forumType).new} karma.
        </div>
      </SingleColumnSection>
    );
  }

  return (
    <SingleColumnSection className={classes.root}>
      {loadingExistingTag && <Loading/>}
      
      {existingTag?.isPlaceholderPage &&
        <p>Some pages already link to this page.</p>
      }

      {createdType === "tag"
        ? <SectionTitle title={`New Wikitag`}/>
        : <SectionTitle title={`New Wiki Page`}/>
      }
      {!loadingExistingTag && (
        <TagForm
          initialData={existingTag ?? undefined}
          prefilledProps={{
            name: prefillName,
            wikiOnly: (createdType === "wiki"),
          }}
          onSuccess={async (tag) => {
            if (existingTag) {
              await updateTag({
                variables: {
                  selector: { _id: existingTag._id },
                  data: { isPlaceholderPage: false }
                }
              });
            }
            navigate({pathname: tagGetUrl(tag)});
          }}
        />
      )}

    </SingleColumnSection>
  );
}

export default NewTagPage;
